import type { itemId } from '../data/types'

export interface MergeRecipe {
    count: number
    drops: Record<string, number>
}

export interface MergeableItemData {
    id: itemId
    merge?: { recipes: readonly MergeRecipe[] }
    chain?: { next: itemId | null }
}

/**
 * Calculates what items should be dropped after a merge based on recipes.
 * Uses a greedy algorithm: matches largest recipe first, loops until no match.
 * Leftover items are returned as themselves.
 *
 * @param count - Number of items being merged
 * @param itemData - Item data with merge recipes and chain info
 * @returns Record of itemId to count, or null if no merge is possible
 *
 * @example
 * // Standard merge: 5 items with recipe [count:5 → 2 next]
 * calculateMergeDrops(5, orb1Data) // => { 'orb-2': 2 }
 *
 * @example
 * // With leftovers: 4 items with recipes [5→2, 3→1]
 * calculateMergeDrops(4, orb1Data) // => { 'orb-2': 1, 'orb-1': 1 }
 *
 * @example
 * // Multiple recipes: 8 items with recipes [5→2, 3→1]
 * calculateMergeDrops(8, orb1Data) // => { 'orb-2': 3 } (5→2, 3→1)
 */
// Calculate what items to drop based on merge count and item data
// Recipes should be ordered by count descending (e.g., 5 before 3)
// Leftover items that don't match any recipe are dropped back as themselves
export function calculateMergeDrops(
    count: number,
    itemData: MergeableItemData
): Record<itemId, number> | null {
    const recipes = itemData.merge?.recipes
    if (!recipes) return null

    const next = itemData.chain?.next ?? null
    const result: Record<itemId, number> = {}
    let remaining = count

    loop: while (remaining > 0) {
        for (const recipe of recipes) {
            if (remaining >= recipe.count) {
                remaining -= recipe.count
                for (let [itemId, dropCount] of Object.entries(recipe.drops)) {
                    // 'next' is a special keyword that resolves to the next tier item
                    if (itemId === 'next' && next) itemId = next
                    result[itemId] = (result[itemId] ?? 0) + dropCount
                }
                continue loop
            }
        }
        if (remaining === count) {
            return null
        }
        // No recipe matched - drop leftover items back as themselves
        result[itemData.id] = (result[itemData.id] ?? 0) + remaining
        break
    }
    return result
}
